import React, { useState } from 'react';
import { Sparkles, ArrowRight, Copy, Check, Globe, Award, Hand, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import BookingCalendar from './BookingCalendar';

const SPECIALTIES = ['Deep Tissue', 'Sports Massage', 'Thai Massage', 'Shiatsu', 'Hot Stone'];

const SHARE_MSG = `Meet Rino — the new Senior Massage Therapist at Dublin Holistic Massage! 20+ years of international experience and a certified Thai Massage professional.\n\nNew clients get 5% off with code TRYRINO — and it stacks with the 5% online deposit discount for 10% off in total!\n\nBook here: https://www.dublinholisticmassages.com`;

const MeetRinoPromo = () => {
  const [copied, setCopied] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText('TRYRINO');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      <section className="py-16 bg-gradient-to-br from-emerald-50 via-sage-50 to-stone-100 relative overflow-hidden" data-testid="meet-rino-promo">
        <div className="absolute top-0 left-0 w-80 h-80 bg-emerald-200/30 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-64 h-64 bg-amber-200/30 rounded-full blur-3xl" />

        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider mb-6">
                <Sparkles size={14} className="text-emerald-600" />
                New to Our Team
              </div>

              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-stone-800 mb-4 leading-tight">
                Meet Rino
                <span className="block italic text-emerald-700 mt-1">Senior Massage Therapist</span>
              </h2>

              <p className="text-stone-600 font-light mb-6 max-w-md leading-relaxed">
                With over 20 years of international experience across Italy, London, Dubai and Ireland's finest spas,
                Rino is a government-certified Thai Massage professional. New clients get{' '}
                <strong className="text-emerald-700">5% off</strong> their first massage with Rino using code{' '}
                <strong className="text-emerald-700">TRYRINO</strong> — and it stacks with the 5% online deposit
                discount for <strong className="text-emerald-700">10% off in total</strong>.
              </p>

              <div className="flex flex-wrap gap-2 mb-6">
                {SPECIALTIES.map((s) => (
                  <span key={s} className="text-xs bg-white/80 text-emerald-800 px-3 py-1 rounded-full border border-emerald-200">{s}</span>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-6">
                <div className="bg-white rounded-2xl border-2 border-dashed border-emerald-300 px-6 py-3 flex items-center gap-3">
                  <Sparkles size={20} className="text-emerald-600" />
                  <span className="font-mono text-xl font-bold tracking-widest text-emerald-700">TRYRINO</span>
                  <button
                    onClick={handleCopy}
                    className="ml-2 text-emerald-400 hover:text-emerald-600 transition-colors"
                    data-testid="copy-tryrino-btn"
                  >
                    {copied ? <Check size={18} className="text-green-500" /> : <Copy size={18} />}
                  </button>
                </div>
                <div className="flex flex-col">
                  <span className="text-2xl font-bold text-emerald-600">5% OFF</span>
                  <span className="text-xs text-emerald-600 font-medium">+5% deposit discount = 10% total</span>
                </div>
              </div>

              <p className="text-xs text-stone-400 mb-4">New clients only. Select Rino as your therapist and enter the code at checkout. Stacks with the 5% online deposit discount.</p>

              <div className="flex items-center gap-3">
                <Button
                  onClick={() => setBookingOpen(true)}
                  className="bg-luxury-green hover:bg-luxury-green/90 text-white rounded-full py-6 px-8 text-sm uppercase tracking-wider font-medium group transition-all"
                  data-testid="try-rino-book-btn"
                >
                  Book with Rino
                  <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button
                  onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(SHARE_MSG)}`, '_blank')}
                  className="bg-luxury-green-600 hover:bg-luxury-green-500 text-white rounded-full w-12 h-12 p-0 flex items-center justify-center"
                  data-testid="rino-whatsapp-share"
                  title="Share Rino's intro offer on WhatsApp"
                >
                  <MessageCircle size={20} />
                </Button>
              </div>
            </div>

            <div className="relative hidden md:block">
              <div className="relative rounded-3xl overflow-hidden shadow-lg">
                <img
                  src="https://customer-assets.emergentagent.com/job_4d2f52c7-22cd-4150-9059-ad99d56ca7fb/artifacts/ngbssild_IMG-20260713-WA0001.jpg"
                  alt="Rino performing a traditional Thai massage stretch"
                  className="w-full h-72 object-cover"
                  data-testid="rino-promo-photo"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
                  <p className="text-white text-sm font-medium">Traditional Thai stretching — Rino's signature technique</p>
                </div>
              </div>
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg -mt-6 mx-6 relative">
                <div className="flex items-center gap-3 mb-3">
                  <Award size={28} className="text-emerald-600 flex-shrink-0" />
                  <h3 className="font-display text-xl text-stone-800">World-Class Hands</h3>
                </div>
                <div className="bg-emerald-50 rounded-xl p-4 text-left space-y-2">
                  <div className="flex items-center gap-2 text-sm text-stone-600">
                    <Check size={14} className="text-emerald-600" />
                    <span>20+ years international experience</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-stone-600">
                    <Check size={14} className="text-emerald-600" />
                    <span>Government-certified Thai Massage professional</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-stone-600">
                    <Hand size={14} className="text-emerald-600" />
                    <span>Sports Science qualified &amp; yoga teacher</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-stone-600">
                    <Globe size={14} className="text-emerald-600" />
                    <span>Speaks English, Italian, Spanish &amp; French</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <BookingCalendar isOpen={bookingOpen} onClose={() => setBookingOpen(false)} />
    </>
  );
};

export default MeetRinoPromo;
